import { useState } from "react";
import { EntryForm } from "../components/EntryForm.tsx";
import { EntryList } from "../components/EntryList.tsx";
import { Loader } from "../components/Loader.tsx";
import { api } from "../lib/api.ts";
import { useIsOwner } from "../lib/auth.ts";
import { addDays, formatLongDate, todayISO } from "../lib/day.ts";
import type { Entry, Mood } from "../lib/types.ts";
import { useAsync } from "../lib/useAsync.ts";

const RANGES = [
  { days: 30, label: "30 días" },
  { days: 90, label: "3 meses" },
  { days: 365, label: "1 año" },
];

/** Agrupa por dia conservando el orden que ya trae la API (mas reciente primero). */
function groupByDay(entries: Entry[]): Array<[string, Entry[]]> {
  const groups = new Map<string, Entry[]>();
  for (const entry of entries) {
    const list = groups.get(entry.date);
    if (list) list.push(entry);
    else groups.set(entry.date, [entry]);
  }
  return [...groups.entries()];
}

export function History() {
  const [days, setDays] = useState(30);
  const [adding, setAdding] = useState(false);
  const isOwner = useIsOwner();

  const { data, error, loading, reload } = useAsync<{
    moods: Mood[];
    entries: Entry[];
  }>(async () => {
    const [moods, entries] = await Promise.all([
      api.moods(),
      api.listEntries({ from: addDays(todayISO(), -(days - 1)), to: todayISO() }),
    ]);
    return { moods, entries };
  }, [days]);

  return (
    <>
      <section className="card">
        <div className="card__head">
          <h2>Historial</h2>
          <div className="segmented" role="group" aria-label="Rango">
            {RANGES.map((range) => (
              <button
                key={range.days}
                className="segmented__item"
                aria-pressed={days === range.days}
                onClick={() => setDays(range.days)}
              >
                {range.label}
              </button>
            ))}
          </div>
        </div>

        {isOwner &&
          (adding && data ? (
            <EntryForm
              moods={data.moods}
              allowDate
              submitLabel="Añadir registro"
              onSubmit={async (input) => {
                await api.createEntry(input);
                setAdding(false);
                reload();
              }}
              onCancel={() => setAdding(false)}
            />
          ) : (
            <button className="btn btn--ghost" onClick={() => setAdding(true)}>
              + Registrar un día pasado
            </button>
          ))}
      </section>

      <Loader loading={loading} error={error}>
        {data && (
          <section className="card">
            {data.entries.length === 0 ? (
              <p className="empty">
                No hay registros en este período.
              </p>
            ) : (
              groupByDay(data.entries).map(([date, entries]) => (
                <div className="day" key={date}>
                  <div className="day__head">
                    <span className="day__title">{formatLongDate(date)}</span>
                    {entries.length > 1 && (
                      <span className="subtle">{entries.length} registros</span>
                    )}
                  </div>
                  <EntryList
                    entries={entries}
                    moods={data.moods}
                    onUpdate={
                      isOwner
                        ? async (id, input) => {
                            await api.updateEntry(id, input);
                            reload();
                          }
                        : undefined
                    }
                    onDelete={
                      isOwner
                        ? async (id) => {
                            await api.deleteEntry(id);
                            reload();
                          }
                        : undefined
                    }
                  />
                </div>
              ))
            )}
          </section>
        )}
      </Loader>
    </>
  );
}
